import { Injectable, Optional, inject } from '@angular/core';
import Keycloak from 'keycloak-js';

import { runtimeConfig } from '../runtime-config';

/**
 * Single answer to "is there a signed-in user, and who".
 *
 * The components used to inject Keycloak directly and gate their templates on
 * `keycloak.authenticated`. That breaks when auth is disabled: no Keycloak
 * provider exists, so injection fails — and even if it did not, `authenticated`
 * would be false and the UI would render its login screen instead of the data
 * it was pointed at.
 *
 * With `runtimeConfig.authEnabled = false` this reports a signed-in local
 * operator, which is the truthful description of that mode: the backend is
 * serving every row without a token, so the UI should show them.
 */
@Injectable({ providedIn: 'root' })
export class SessionUserService {
  // Optional because with auth disabled there is no Keycloak provider at all.
  private readonly keycloak = inject(Keycloak, { optional: true });

  /** Whether the UI should render the application rather than a login screen. */
  get authenticated(): boolean {
    if (!runtimeConfig.authEnabled) return true;
    return !!this.keycloak?.authenticated;
  }

  get email(): string {
    if (!runtimeConfig.authEnabled) return 'local dev';
    return (this.keycloak?.tokenParsed?.['email'] as string) ?? '';
  }

  /** The realm the user picked, if any. Null when auth is off. */
  get realm(): string | null {
    if (!runtimeConfig.authEnabled) return null;
    return localStorage.getItem('agRealm');
  }

  login(redirectUri = window.location.origin): void {
    if (!runtimeConfig.authEnabled) return;
    void this.keycloak?.login({ redirectUri });
  }

  logout(redirectUri = window.location.origin): void {
    if (!runtimeConfig.authEnabled) return;
    void this.keycloak?.logout({ redirectUri });
  }

  /** True when a login/logout control should be shown at all. */
  get authAvailable(): boolean {
    return runtimeConfig.authEnabled;
  }
}
